/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { motion } from 'framer-motion';
import { styles } from '../FaceReportStyles';
import VibeMap from '../../../components/VibeMap';

const getGapInsight = (gapPercent, t) => {
    if (gapPercent >= 60) {
        return {
            title: t('face_result.detail.vibe.large_gap_title'),
            body: t('face_result.detail.vibe.large_gap_body'),
        };
    }

    if (gapPercent <= 30) {
        return {
            title: t('face_result.detail.vibe.small_gap_title'),
            body: t('face_result.detail.vibe.small_gap_body'),
        };
    }

    return {
        title: t('face_result.detail.vibe.medium_gap_title'),
        body: t('face_result.detail.vibe.medium_gap_body'),
    };
};

const VibeAnalysisSection = ({
    hasIntegrated,
    physical,
    target,
    matchType,
    congruenceScore,
    chugumiResult,
    handleIntegrate,
    integrating,
}) => {
    const { t } = useTranslation();
    const [animatedGap, setAnimatedGap] = useState(0);

    const distance = useMemo(() => {
        const dx = (physical?.x || 0) - (target?.x || 0);
        const dy = (physical?.y || 0) - (target?.y || 0);
        return Math.sqrt((dx ** 2) + (dy ** 2));
    }, [physical?.x, physical?.y, target?.x, target?.y]);

    const gapPercent = useMemo(() => {
        if (!hasIntegrated) return 0;
        return Math.min((distance / 141.4) * 100, 100);
    }, [distance, hasIntegrated]);

    const gapInsight = useMemo(() => getGapInsight(gapPercent, t), [gapPercent, t]);
    const safeCongruence = Number.isFinite(Number(congruenceScore)) ? Number(congruenceScore).toFixed(1) : '-';

    useEffect(() => {
        if (!hasIntegrated) return undefined;

        let start = 0;
        const increment = gapPercent / 40 || 0;
        const timer = setInterval(() => {
            start += increment;
            if (start >= gapPercent) {
                setAnimatedGap(gapPercent);
                clearInterval(timer);
            } else {
                setAnimatedGap(start);
            }
        }, 30);

        return () => clearInterval(timer);
    }, [gapPercent, hasIntegrated]);

    if (!hasIntegrated) {
        if (!chugumiResult) return null;

        return (
            <div style={styles.integrationOffer}>
                <h3>{t('face_result.ui.integration_available')}</h3>
                <p>
                    <Trans
                        i18nKey="face_result.ui.integration_desc"
                        values={{ type: chugumiResult.summary?.mbti || t('common.chugumi') }}
                    />
                </p>
                <button
                    onClick={() => handleIntegrate(false)}
                    disabled={integrating}
                    style={styles.integrateBtn}
                >
                    {integrating ? t('face_result.ui.analyzing') : t('face_result.ui.integrate_now')}
                </button>
            </div>
        );
    }

    return (
        <div style={styles.section}>
            <div style={summaryCardStyle}>
                <div style={eyebrowStyle}>{t('face_result.detail.visual.completion')}</div>
                <div style={headlineStyle}>
                    {t('face_result.detail.vibe.sync', { type: matchType || t('face_result.detail.vibe.current_mood'), score: safeCongruence })}
                </div>
                <p style={bodyStyle}>
                    {t('face_result.detail.vibe.sync_description')}
                </p>
            </div>

            <div style={summaryCardStyle}>
                <div style={eyebrowStyle}>{t('face_result.detail.vibe.map')}</div>
                <p style={{ ...bodyStyle, marginBottom: '1rem' }}>
                    {t('face_result.detail.vibe.map_description')}
                </p>
                <VibeMap physical={physical} target={target} matchType={matchType} />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, margin: '-50px' }}
                style={summaryCardStyle}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                        <div style={eyebrowStyle}>{t('face_result.detail.visual.direction')}</div>
                        <div style={headlineStyle}>{gapInsight.title}</div>
                    </div>
                    <div style={gapValueStyle}>{animatedGap.toFixed(1)}%</div>
                </div>

                <div style={barTrackStyle}>
                    <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${gapPercent}%` }}
                        transition={{ duration: 1.2, ease: 'easeOut' }}
                        viewport={{ once: true }}
                        style={barFillStyle}
                    />
                </div>

                <p style={{ ...bodyStyle, marginTop: '0.9rem' }}>{gapInsight.body}</p>
            </motion.div>
        </div>
    );
};

const summaryCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '18px',
    padding: '1rem',
    marginBottom: '1rem',
};

const eyebrowStyle = {
    fontSize: '0.78rem',
    color: '#60a5fa',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '0.45rem',
};

const headlineStyle = {
    color: '#f8fafc',
    fontWeight: 800,
    fontSize: '1rem',
    lineHeight: 1.4,
};

const bodyStyle = {
    color: '#cbd5e1',
    fontSize: '0.88rem',
    lineHeight: 1.65,
    margin: '0.45rem 0 0 0',
};

const gapValueStyle = {
    fontSize: '1.6rem',
    fontWeight: 900,
    color: '#f8fafc',
};

const barTrackStyle = {
    height: '10px',
    width: '100%',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '999px',
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.05)',
};

const barFillStyle = {
    height: '100%',
    background: 'linear-gradient(90deg, #60a5fa, #f472b6, #fbbf24)',
    borderRadius: '999px',
};

export default VibeAnalysisSection;
